import { useEffect, useRef, useState } from "react";
import {
  Bold,
  Italic,
  Underline,
  Heading2,
  Heading3,
  List,
  ListOrdered,
  Link2,
  Pilcrow,
  Eraser,
} from "lucide-react";
import { sanitizeHtml } from "@/lib/sanitize";

function ToolbarBtn({ label, icon: Icon, onClick, isActive, testid }) {
  return (
    <button
      type="button"
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      aria-label={label}
      title={label}
      data-testid={testid}
      className={`inline-flex h-7 w-7 items-center justify-center rounded-md border text-slate-700 hover:bg-slate-50 ${
        isActive
          ? "border-slate-900 bg-slate-100 text-slate-900"
          : "border-slate-200 bg-white"
      }`}
    >
      <Icon className="h-3.5 w-3.5" />
    </button>
  );
}

/**
 * Minimal contentEditable WYSIWYG editor. Backend HTML sanitiser is the
 * source of truth — the toolbar produces a small whitelisted subset of
 * tags (p, strong, em, u, h2, h3, ul/ol, li, a).
 */
export default function RichTextEditor({ value, onChange, placeholder, testid }) {
  const ref = useRef(null);
  const [active, setActive] = useState({});

  // Sync external `value` into the editable div only when it diverges
  // from the current DOM (prevents caret jumps on every keystroke).
  // Defence-in-depth: sanitise even our own value before assigning innerHTML.
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const safe = sanitizeHtml(value || "");
    if (safe !== el.innerHTML) {
      el.innerHTML = safe;
    }
  }, [value]);

  const refreshState = () => {
    try {
      setActive({
        bold: document.queryCommandState("bold"),
        italic: document.queryCommandState("italic"),
        underline: document.queryCommandState("underline"),
      });
    } catch {
      /* queryCommandState is deprecated; ignore failures */
    }
  };

  const exec = (cmd, arg) => {
    document.execCommand(cmd, false, arg);
    ref.current?.focus();
    refreshState();
    onChange?.(ref.current?.innerHTML || "");
  };

  const onInput = () => {
    onChange?.(ref.current?.innerHTML || "");
    refreshState();
  };

  const onPaste = (e) => {
    // Strip rich formatting on paste — we want plain text into a clean container.
    e.preventDefault();
    const text = (e.clipboardData || window.clipboardData).getData("text/plain");
    document.execCommand("insertText", false, text);
  };

  const promptLink = () => {
    // eslint-disable-next-line no-alert
    const url = window.prompt("Link URL (https://… or /path)");
    if (!url) return;
    exec("createLink", url);
  };

  return (
    <div className="rounded-xl border border-slate-200 bg-white" data-testid={testid}>
      <div className="flex flex-wrap items-center gap-1 border-b border-slate-200 px-2 py-1.5">
        <ToolbarBtn label="Bold" icon={Bold} onClick={() => exec("bold")} isActive={active.bold} testid={`${testid}-bold`} />
        <ToolbarBtn label="Italic" icon={Italic} onClick={() => exec("italic")} isActive={active.italic} testid={`${testid}-italic`} />
        <ToolbarBtn label="Underline" icon={Underline} onClick={() => exec("underline")} isActive={active.underline} testid={`${testid}-underline`} />
        <span className="mx-1 h-4 w-px bg-slate-200" />
        <ToolbarBtn label="Paragraph" icon={Pilcrow} onClick={() => exec("formatBlock", "P")} testid={`${testid}-paragraph`} />
        <ToolbarBtn label="Heading 2" icon={Heading2} onClick={() => exec("formatBlock", "H2")} testid={`${testid}-h2`} />
        <ToolbarBtn label="Heading 3" icon={Heading3} onClick={() => exec("formatBlock", "H3")} testid={`${testid}-h3`} />
        <span className="mx-1 h-4 w-px bg-slate-200" />
        <ToolbarBtn label="Bulleted list" icon={List} onClick={() => exec("insertUnorderedList")} testid={`${testid}-ul`} />
        <ToolbarBtn label="Numbered list" icon={ListOrdered} onClick={() => exec("insertOrderedList")} testid={`${testid}-ol`} />
        <ToolbarBtn label="Link" icon={Link2} onClick={promptLink} testid={`${testid}-link`} />
        <span className="mx-1 h-4 w-px bg-slate-200" />
        <ToolbarBtn label="Clear formatting" icon={Eraser} onClick={() => exec("removeFormat")} testid={`${testid}-clear`} />
      </div>
      <div
        ref={ref}
        role="textbox"
        aria-multiline="true"
        contentEditable
        suppressContentEditableWarning
        onInput={onInput}
        onKeyUp={refreshState}
        onMouseUp={refreshState}
        onPaste={onPaste}
        data-placeholder={placeholder || "Write something…"}
        className="prose prose-sm max-w-none min-h-[180px] px-4 py-3 text-sm text-slate-800 focus:outline-none [&_h2]:text-base [&_h2]:font-semibold [&_h3]:text-sm [&_h3]:font-semibold [&_a]:text-sky-700 [&_a]:underline empty:before:content-[attr(data-placeholder)] empty:before:text-slate-400"
        data-testid={`${testid}-editable`}
      />
    </div>
  );
}
